import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  Save, Upload, Trash2, Plus, Image as ImageIcon, 
  ChevronDown, ChevronUp, X, Edit2, Eye, Building2, 
  Calendar, FileText, Users, Coffee, Bold, Italic, 
  Underline as UnderlineIcon, List, ListOrdered, Link as LinkIcon,
  ExternalLink, GripVertical
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import UnderlineExt from '@tiptap/extension-underline';
import { ImageDropzone, GalleryDropzone } from '@/components/ImageDropzone';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Sortable Room Item Component
const SortableRoomItem = ({ room, roomIndex, sectionIndex, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `room-${roomIndex}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="border border-gray-200 rounded-lg p-4 flex items-center gap-4 group hover:border-gray-400 transition-colors bg-white"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
      >
        <GripVertical size={20} />
      </button>
      {room.image_url ? (
        <img src={room.image_url} className="w-20 h-20 object-cover rounded" alt="" />
      ) : (
        <div className="w-20 h-20 bg-gray-100 rounded flex items-center justify-center">
          <ImageIcon className="w-8 h-8 text-gray-300" />
        </div>
      )}
      <div className="flex-1">
        <h5 className="font-bold">{room.title}</h5>
        <p className="text-sm text-gray-500">{room.capacity}</p>
        <p className="text-xs text-gray-400">{room.popup_gallery_images?.length || 0} Galerie-Bilder</p>
      </div>
      <div className="flex gap-2">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => onEdit(sectionIndex, roomIndex, room)}
          className="gap-2"
        >
          <Edit2 size={14} /> Bearbeiten
        </Button>
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => onDelete(sectionIndex, roomIndex)}
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 size={16} />
        </Button>
      </div>
    </div>
  );
};

// Sortable Tenant Item Component
const SortableTenantItem = ({ tenant, tenantIndex, sectionIndex, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `tenant-${tenantIndex}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style}
      className="border border-gray-200 rounded-lg p-4 flex items-center gap-4 group hover:border-gray-400 transition-colors bg-white"
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
      >
        <GripVertical size={16} />
      </button>
      {tenant.logo_url ? (
        <img src={tenant.logo_url} className="w-12 h-12 object-contain rounded" alt="" />
      ) : (
        <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center">
          <Building2 className="w-5 h-5 text-gray-300" />
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h5 className="font-bold truncate">{tenant.name}</h5>
        {tenant.url && (
          <a href={tenant.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-1">
            <ExternalLink size={10} /> Website
          </a>
        )}
      </div>
      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => onEdit(sectionIndex, tenantIndex, tenant)}
        >
          <Edit2 size={14} />
        </Button>
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => onDelete(sectionIndex, tenantIndex)}
          className="text-red-500 hover:text-red-700 hover:bg-red-50"
        >
          <Trash2 size={14} />
        </Button>
      </div>
    </div>
  );
};

// WYSIWYG Editor Component
const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      UnderlineExt,
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-1 p-2 bg-gray-50 border-b border-gray-200">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-200' : ''}`}
        >
          <Bold size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-200' : ''}`}
        >
          <Italic size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('underline') ? 'bg-gray-200' : ''}`}
        >
          <UnderlineIcon size={16} />
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-200' : ''}`}
        >
          <List size={16} />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-200' : ''}`}
        >
          <ListOrdered size={16} />
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => {
            const url = window.prompt('Link URL:');
            if (url) {
              editor.chain().focus().setLink({ href: url }).run();
            }
          }}
          className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-gray-200' : ''}`}
        >
          <LinkIcon size={16} />
        </button>
      </div>
      {/* Editor */}
      <EditorContent 
        editor={editor} 
        className="prose prose-sm max-w-none p-3 min-h-[120px] focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[100px]"
      />
    </div>
  );
};

export const ContentManager = () => {
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedSection, setExpandedSection] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [roomDialogOpen, setRoomDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchPage();
  }, []);

  const fetchPage = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pages/home`);
      setPage(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Inhalte");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('token');
    try {
      await axios.put(
        `${BACKEND_URL}/api/pages/home`, 
        page,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Änderungen gespeichert!");
    } catch (error) {
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const updateSection = (index, field, value) => {
    const newSections = [...page.sections];
    newSections[index] = { ...newSections[index], [field]: value };
    setPage({ ...page, sections: newSections });
  };

  const updateSectionSettings = (index, field, value) => {
    const newSections = [...page.sections];
    newSections[index].settings = { ...newSections[index].settings, [field]: value };
    setPage({ ...page, sections: newSections });
  };

  // Handle drag end for rooms
  const handleRoomDragEnd = (event, sectionIndex) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = parseInt(active.id.split('-')[1]);
      const newIndex = parseInt(over.id.split('-')[1]);
      
      const newSections = [...page.sections];
      newSections[sectionIndex].items = arrayMove(
        newSections[sectionIndex].items,
        oldIndex,
        newIndex
      );
      setPage({ ...page, sections: newSections });
      toast.success('Reihenfolge geändert - Vergessen Sie nicht zu speichern!');
    }
  };

  // Handle drag end for tenants
  const handleTenantDragEnd = (event, sectionIndex) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = parseInt(active.id.split('-')[1]);
      const newIndex = parseInt(over.id.split('-')[1]);
      
      const newSections = [...page.sections];
      newSections[sectionIndex].items = arrayMove(
        newSections[sectionIndex].items,
        oldIndex,
        newIndex
      );
      setPage({ ...page, sections: newSections });
      toast.success('Reihenfolge geändert - Vergessen Sie nicht zu speichern!');
    }
  };

  const getSectionIcon = (type) => {
    switch(type) {
      case 'hero': return <Eye size={18} />;
      case 'text_image': return <FileText size={18} />;
      case 'rooms_grid': return <Building2 size={18} />;
      case 'features': return <Users size={18} />;
      case 'tenants_grid': return <Coffee size={18} />;
      default: return <FileText size={18} />;
    }
  };

  const getSectionLabel = (section) => {
    if (section.title?.toLowerCase().includes('veranstaltung') || section.title?.toLowerCase().includes('event')) {
      return null;
    }
    switch(section.type) {
      case 'hero': return 'Startseite Hero';
      case 'rooms_grid': return 'Besprechungsräume';
      case 'features': return 'Vorteile & Benefits';
      case 'tenants_grid': return 'Bereits Teil vom teamWERK';
      default: return section.title || section.type;
    }
  };

  // Filter out events section
  const filteredSections = page?.sections?.filter(section => {
    const title = section.title?.toLowerCase() || '';
    return !title.includes('veranstaltung') && !title.includes('event');
  });

  // Add new room
  const addNewRoom = (sectionIndex) => {
    const newRoom = {
      title: 'Neuer Raum',
      capacity: '',
      description: '',
      description_en: '',
      image_url: '',
      cta_link: '',
      cta_text: 'Jetzt buchen',
      popup_content: '',
      popup_content_en: '',
      popup_gallery_images: [],
      features: []
    };
    setEditingRoom({ sectionIndex, roomIndex: -1, room: newRoom, isNew: true });
    setRoomDialogOpen(true);
  };

  // Delete room
  const deleteRoom = (sectionIndex, roomIndex) => {
    if (!window.confirm('Diesen Raum wirklich löschen?')) return;
    const newSections = [...page.sections];
    newSections[sectionIndex].items.splice(roomIndex, 1);
    setPage({ ...page, sections: newSections });
    toast.success('Raum gelöscht - Vergessen Sie nicht zu speichern!');
  };

  // Add new tenant
  const addNewTenant = (sectionIndex) => {
    const newTenant = {
      name: 'Neuer Mieter',
      url: '',
      logo_url: '',
      description: ''
    };
    setEditingTenant({ sectionIndex, tenantIndex: -1, tenant: newTenant, isNew: true });
    setTenantDialogOpen(true);
  };

  // Delete tenant
  const deleteTenant = (sectionIndex, tenantIndex) => {
    if (!window.confirm('Diesen Mieter wirklich löschen?')) return;
    const newSections = [...page.sections];
    newSections[sectionIndex].items.splice(tenantIndex, 1);
    setPage({ ...page, sections: newSections });
    toast.success('Mieter gelöscht - Vergessen Sie nicht zu speichern!');
  };

  if (loading) return (
    <div className="p-8 text-center">
      <div className="animate-spin w-8 h-8 border-2 border-black border-t-transparent rounded-full mx-auto"></div>
      <p className="mt-4 text-gray-500">Laden...</p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center sticky top-0 z-10">
        <div>
          <h1 className="text-2xl font-bold">Inhalte bearbeiten</h1>
          <p className="text-sm text-gray-500">Texte, Bilder und Bereiche der Website anpassen</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-black hover:bg-gray-800 gap-2">
          <Save size={16} /> {saving ? 'Speichern...' : 'Änderungen speichern'}
        </Button>
      </div>

      {/* Help text */}
      <div className="p-6 max-w-5xl mx-auto">
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Tipp:</strong> Klicken Sie auf einen Bereich um ihn zu bearbeiten. Bilder können per Drag & Drop hochgeladen werden!
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="px-6 pb-6 max-w-5xl mx-auto space-y-3">
        {filteredSections?.map((section, sectionIndex) => {
          const label = getSectionLabel(section);
          if (!label) return null;
          
          const actualIndex = page.sections.findIndex(s => s === section);
          
          return (
          <div key={sectionIndex} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
            {/* Section Header */}
            <div 
              className="flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => setExpandedSection(expandedSection === actualIndex ? null : actualIndex)}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-gray-100 rounded-md text-gray-600">
                  {getSectionIcon(section.type)}
                </div>
                <div>
                  <h3 className="font-bold text-black">{label}</h3>
                  <p className="text-xs text-gray-400">Klicken zum Bearbeiten</p>
                </div>
              </div>
              {expandedSection === actualIndex ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>

            {/* Section Content */}
            {expandedSection === actualIndex && (
              <div className="border-t border-gray-200 p-6 space-y-6">
                
                {/* TEXT_IMAGE Sections */}
                {section.type === 'text_image' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Titel (DE)</label>
                        <Input 
                          value={section.title || ''}
                          onChange={(e) => updateSection(actualIndex, 'title', e.target.value)}
                          className="font-bold"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Titel (EN)</label>
                        <Input 
                          value={section.title_en || ''}
                          onChange={(e) => updateSection(actualIndex, 'title_en', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Untertitel (DE)</label>
                        <Input 
                          value={section.subtitle || ''}
                          onChange={(e) => updateSection(actualIndex, 'subtitle', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Untertitel (EN)</label>
                        <Input 
                          value={section.subtitle_en || ''}
                          onChange={(e) => updateSection(actualIndex, 'subtitle_en', e.target.value)}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Inhalt (DE)</label>
                      <RichTextEditor 
                        value={section.content || ''}
                        onChange={(value) => updateSection(actualIndex, 'content', value)}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Inhalt (EN)</label>
                      <RichTextEditor 
                        value={section.content_en || ''}
                        onChange={(value) => updateSection(actualIndex, 'content_en', value)}
                      />
                    </div>

                    {/* Image Upload with Drag & Drop */}
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Bild</label>
                      <ImageDropzone
                        value={section.image_url}
                        onChange={(url) => updateSection(actualIndex, 'image_url', url)}
                        placeholder="Bild hierher ziehen oder klicken"
                        previewHeight="h-48"
                      />
                    </div>

                    {/* Button Settings */}
                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Button Text (DE)</label>
                        <Input 
                          value={section.settings?.cta_text || ''}
                          onChange={(e) => updateSectionSettings(actualIndex, 'cta_text', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Button Text (EN)</label>
                        <Input 
                          value={section.settings?.cta_text_en || ''}
                          onChange={(e) => updateSectionSettings(actualIndex, 'cta_text_en', e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Popup Content with Gallery */}
                    {section.settings?.cta_type === 'popup' && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-2">Popup Inhalt (DE)</label>
                          <RichTextEditor 
                            value={section.settings?.popup_content || ''}
                            onChange={(value) => updateSectionSettings(actualIndex, 'popup_content', value)}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-2">Popup Inhalt (EN)</label>
                          <RichTextEditor 
                            value={section.settings?.popup_content_en || ''}
                            onChange={(value) => updateSectionSettings(actualIndex, 'popup_content_en', value)}
                          />
                        </div>

                        {/* Popup Gallery with Drag & Drop */}
                        <div>
                          <label className="block text-sm font-medium text-gray-600 mb-2">Popup Galerie</label>
                          <GalleryDropzone
                            images={section.settings?.popup_gallery_images || []}
                            onChange={(images) => updateSectionSettings(actualIndex, 'popup_gallery_images', images)}
                            maxImages={20}
                          />
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* ROOMS_GRID Section */}
                {section.type === 'rooms_grid' && (
                  <>
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Titel (DE)</label>
                        <Input 
                          value={section.title || ''}
                          onChange={(e) => updateSection(actualIndex, 'title', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Titel (EN)</label>
                        <Input 
                          value={section.title_en || ''}
                          onChange={(e) => updateSection(actualIndex, 'title_en', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h4 className="font-bold text-black">Räume verwalten</h4>
                        <p className="text-xs text-gray-400">Zum Sortieren ziehen</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => addNewRoom(actualIndex)}
                        className="gap-2"
                      >
                        <Plus size={16} /> Raum hinzufügen
                      </Button>
                    </div>
                    
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event) => handleRoomDragEnd(event, actualIndex)}
                    >
                      <SortableContext
                        items={section.items?.map((_, idx) => `room-${idx}`) || []}
                        strategy={verticalListSortingStrategy}
                      >
                        <div className="space-y-3">
                          {section.items?.map((room, roomIndex) => (
                            <SortableRoomItem
                              key={roomIndex}
                              room={room}
                              roomIndex={roomIndex}
                              sectionIndex={actualIndex}
                              onEdit={(si, ri, r) => {
                                setEditingRoom({ sectionIndex: si, roomIndex: ri, room: {...r}, isNew: false });
                                setRoomDialogOpen(true);
                              }}
                              onDelete={deleteRoom}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </>
                )}

                {/* TENANTS_GRID Section - "Bereits Teil vom teamWERK" */}
                {section.type === 'tenants_grid' && (
                  <>
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Titel (DE)</label>
                        <Input 
                          value={section.title || ''}
                          onChange={(e) => updateSection(actualIndex, 'title', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Titel (EN)</label>
                        <Input 
                          value={section.title_en || ''}
                          onChange={(e) => updateSection(actualIndex, 'title_en', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="flex justify-between items-center mb-4">
                      <div>
                        <h4 className="font-bold text-black">Mieter verwalten</h4>
                        <p className="text-xs text-gray-400">Zum Sortieren ziehen</p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => addNewTenant(actualIndex)}
                        className="gap-2"
                      >
                        <Plus size={16} /> Mieter hinzufügen
                      </Button>
                    </div>
                    
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={(event) => handleTenantDragEnd(event, actualIndex)}
                    >
                      <SortableContext
                        items={section.items?.map((_, idx) => `tenant-${idx}`) || []}
                        strategy={rectSortingStrategy}
                      >
                        <div className="grid grid-cols-2 gap-3">
                          {section.items?.map((tenant, tenantIndex) => (
                            <SortableTenantItem
                              key={tenantIndex}
                              tenant={tenant}
                              tenantIndex={tenantIndex}
                              sectionIndex={actualIndex}
                              onEdit={(si, ti, t) => {
                                setEditingTenant({ sectionIndex: si, tenantIndex: ti, tenant: {...t}, isNew: false });
                                setTenantDialogOpen(true);
                              }}
                              onDelete={deleteTenant}
                            />
                          ))}
                        </div>
                      </SortableContext>
                    </DndContext>
                  </>
                )}

                {/* FEATURES Section */}
                {section.type === 'features' && (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Titel (DE)</label>
                        <Input 
                          value={section.title || ''}
                          onChange={(e) => updateSection(actualIndex, 'title', e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-2">Titel (EN)</label>
                        <Input 
                          value={section.title_en || ''}
                          onChange={(e) => updateSection(actualIndex, 'title_en', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-600 mb-2">Benefits bearbeiten</label>
                      <div className="grid grid-cols-2 gap-3">
                        {section.items?.map((item, itemIndex) => (
                          <div key={itemIndex} className="border border-gray-200 rounded p-3">
                            <Input 
                              value={item.title || ''}
                              onChange={(e) => {
                                const newSections = [...page.sections];
                                newSections[actualIndex].items[itemIndex].title = e.target.value;
                                setPage({ ...page, sections: newSections });
                              }}
                              placeholder="Titel (DE)"
                              className="mb-2 font-bold"
                            />
                            <Input 
                              value={item.description || ''}
                              onChange={(e) => {
                                const newSections = [...page.sections];
                                newSections[actualIndex].items[itemIndex].description = e.target.value;
                                setPage({ ...page, sections: newSections });
                              }}
                              placeholder="Beschreibung (DE)"
                              className="text-sm"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}

                {/* HERO Section */}
                {section.type === 'hero' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Haupttitel</label>
                      <Input 
                        value={section.title || ''}
                        onChange={(e) => updateSection(actualIndex, 'title', e.target.value)}
                        className="text-2xl font-bold"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-2">Untertitel</label>
                      <Input 
                        value={section.subtitle || ''}
                        onChange={(e) => updateSection(actualIndex, 'subtitle', e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
        })}
      </div>

      {/* Room Edit Dialog */}
      <Dialog open={roomDialogOpen} onOpenChange={setRoomDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingRoom?.isNew ? 'Neuen Raum hinzufügen' : `Raum bearbeiten: ${editingRoom?.room?.title}`}
            </DialogTitle>
          </DialogHeader>
          
          {editingRoom && (
            <Tabs defaultValue="general" className="mt-4">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="general">Allgemein</TabsTrigger>
                <TabsTrigger value="details">Details</TabsTrigger>
                <TabsTrigger value="gallery">Galerie</TabsTrigger>
              </TabsList>

              <TabsContent value="general" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Titel</label>
                    <Input 
                      value={editingRoom.room.title || ''}
                      onChange={(e) => setEditingRoom({
                        ...editingRoom,
                        room: {...editingRoom.room, title: e.target.value}
                      })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Kapazität</label>
                    <Input 
                      value={editingRoom.room.capacity || ''}
                      onChange={(e) => setEditingRoom({
                        ...editingRoom,
                        room: {...editingRoom.room, capacity: e.target.value}
                      })}
                      placeholder="z.B. Bis 8 Personen"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Beschreibung (DE)</label>
                  <Input 
                    value={editingRoom.room.description || ''}
                    onChange={(e) => setEditingRoom({
                      ...editingRoom,
                      room: {...editingRoom.room, description: e.target.value}
                    })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Beschreibung (EN)</label>
                  <Input 
                    value={editingRoom.room.description_en || ''}
                    onChange={(e) => setEditingRoom({
                      ...editingRoom,
                      room: {...editingRoom.room, description_en: e.target.value}
                    })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Buchungs-Link</label>
                  <Input 
                    value={editingRoom.room.cta_link || ''}
                    onChange={(e) => setEditingRoom({
                      ...editingRoom,
                      room: {...editingRoom.room, cta_link: e.target.value}
                    })}
                    placeholder="https://anny.co/book/..."
                  />
                </div>

                {/* Main Image with Drag & Drop */}
                <div>
                  <label className="block text-sm font-medium mb-2">Hauptbild</label>
                  <ImageDropzone
                    value={editingRoom.room.image_url}
                    onChange={(url) => setEditingRoom({
                      ...editingRoom,
                      room: {...editingRoom.room, image_url: url}
                    })}
                    placeholder="Hauptbild hochladen"
                    previewHeight="h-40"
                  />
                </div>
              </TabsContent>

              <TabsContent value="details" className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Popup Inhalt (DE)</label>
                  <RichTextEditor 
                    value={editingRoom.room.popup_content || ''}
                    onChange={(value) => setEditingRoom({
                      ...editingRoom,
                      room: {...editingRoom.room, popup_content: value}
                    })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Popup Inhalt (EN)</label>
                  <RichTextEditor 
                    value={editingRoom.room.popup_content_en || ''}
                    onChange={(value) => setEditingRoom({
                      ...editingRoom,
                      room: {...editingRoom.room, popup_content_en: value}
                    })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Features (kommagetrennt)</label>
                  <Input 
                    value={editingRoom.room.features?.join(', ') || ''}
                    onChange={(e) => setEditingRoom({
                      ...editingRoom,
                      room: {...editingRoom.room, features: e.target.value.split(',').map(f => f.trim()).filter(f => f)}
                    })}
                    placeholder="WLAN, Beamer, Whiteboard, ..."
                  />
                </div>
              </TabsContent>

              <TabsContent value="gallery" className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Galerie-Bilder</label>
                  <GalleryDropzone
                    images={editingRoom.room.popup_gallery_images || []}
                    onChange={(images) => setEditingRoom({
                      ...editingRoom,
                      room: {...editingRoom.room, popup_gallery_images: images}
                    })}
                    maxImages={20}
                  />
                </div>
              </TabsContent>
            </Tabs>
          )}

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setRoomDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={() => {
                const newSections = [...page.sections];
                if (editingRoom.isNew) {
                  // Add new room
                  if (!newSections[editingRoom.sectionIndex].items) {
                    newSections[editingRoom.sectionIndex].items = [];
                  }
                  newSections[editingRoom.sectionIndex].items.push(editingRoom.room);
                } else {
                  // Update existing room
                  newSections[editingRoom.sectionIndex].items[editingRoom.roomIndex] = editingRoom.room;
                }
                setPage({ ...page, sections: newSections });
                setRoomDialogOpen(false);
                toast.success(editingRoom.isNew ? "Raum hinzugefügt - Vergessen Sie nicht zu speichern!" : "Raum aktualisiert - Vergessen Sie nicht zu speichern!");
              }}
              className="bg-black hover:bg-gray-800"
            >
              {editingRoom?.isNew ? 'Hinzufügen' : 'Übernehmen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Tenant Edit Dialog */}
      <Dialog open={tenantDialogOpen} onOpenChange={setTenantDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingTenant?.isNew ? 'Neuen Mieter hinzufügen' : `Mieter bearbeiten: ${editingTenant?.tenant?.name}`}
            </DialogTitle>
          </DialogHeader>
          
          {editingTenant && (
            <div className="space-y-4 mt-4">
              <div>
                <label className="block text-sm font-medium mb-2">Name / Firma</label>
                <Input 
                  value={editingTenant.tenant.name || ''}
                  onChange={(e) => setEditingTenant({
                    ...editingTenant,
                    tenant: {...editingTenant.tenant, name: e.target.value}
                  })}
                  placeholder="z.B. Bürokult"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Website URL</label>
                <Input 
                  value={editingTenant.tenant.url || ''}
                  onChange={(e) => setEditingTenant({
                    ...editingTenant,
                    tenant: {...editingTenant.tenant, url: e.target.value}
                  })}
                  placeholder="https://..."
                />
              </div>

              {/* Logo with Drag & Drop */}
              <div>
                <label className="block text-sm font-medium mb-2">Logo</label>
                <ImageDropzone
                  value={editingTenant.tenant.logo_url}
                  onChange={(url) => setEditingTenant({
                    ...editingTenant,
                    tenant: {...editingTenant.tenant, logo_url: url}
                  })}
                  placeholder="Logo hochladen"
                  previewHeight="h-32"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Beschreibung (optional)</label>
                <textarea 
                  className="w-full p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent min-h-[80px] resize-none"
                  value={editingTenant.tenant.description || ''}
                  onChange={(e) => setEditingTenant({
                    ...editingTenant,
                    tenant: {...editingTenant.tenant, description: e.target.value}
                  })}
                  placeholder="Kurze Beschreibung des Unternehmens..."
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setTenantDialogOpen(false)}>
              Abbrechen
            </Button>
            <Button 
              onClick={() => {
                const newSections = [...page.sections];
                if (editingTenant.isNew) {
                  if (!newSections[editingTenant.sectionIndex].items) {
                    newSections[editingTenant.sectionIndex].items = [];
                  }
                  newSections[editingTenant.sectionIndex].items.push(editingTenant.tenant);
                } else {
                  newSections[editingTenant.sectionIndex].items[editingTenant.tenantIndex] = editingTenant.tenant;
                }
                setPage({ ...page, sections: newSections });
                setTenantDialogOpen(false);
                toast.success(editingTenant.isNew ? "Mieter hinzugefügt - Vergessen Sie nicht zu speichern!" : "Mieter aktualisiert - Vergessen Sie nicht zu speichern!");
              }}
              className="bg-black hover:bg-gray-800"
            >
              {editingTenant?.isNew ? 'Hinzufügen' : 'Übernehmen'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentManager;
